import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, concatMap, Observable, of, Subject, take, tap } from 'rxjs';

import { EditAccountUserRequest } from '@shared/account/models';
import { Location } from '@shared/location/models';
import { EditUserAccessComponent } from '@tablesready/edit-user-access';
import { UserWithAccount } from '@tablesready/common-ui';
import { UsersListService } from '../components/users-list/users-list.service';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { AccountApiService } from '@tablesready/frontend-store';

@Injectable()
export class EditUserAccessService {
  constructor(
    private readonly router: Router,
    private readonly modalController: FrontendModalService,
    private readonly accountApiService: AccountApiService,
    private readonly usersListService: UsersListService,
    private readonly alertsService: AlertsService,
    private readonly locationSwitcherService: LocationSwitcherService,
  ) {}

  editUserAccess(user: UserWithAccount): Observable<unknown> {
    const subject = new Subject();
    this.getLocations(([users, locations]) => {
      this.modalController.create<EditUserAccessComponent, unknown>(EditUserAccessComponent, {
        user: users.find(item => item.id === user.id),
        locations: locations.map(loc => ({
          id: loc._id,
          locationName: loc.name,
          enabled: loc.enabled,
        })),
        onNavigatedToLocations: (): void => {
          this.router.navigateByUrl('/settings/account/locations');
        },
        onUserUpdate: (instance: EditUserAccessComponent, accountUserId: string, data: EditAccountUserRequest): void => {
          this.accountApiService.updateAccountUser(accountUserId, data)
            .pipe(
              take(1),
              tap(() => {
                this.alertsService.addSuccess('user updated', 'Success: ');
              }),
              catchError(() => {
                this.alertsService.addSuccess('an error occurred during updating user');

                return of(null);
              }),
            )
            .subscribe(() => {
              this.updateInstance(instance, user.id);
            });
        },
        onRemoveFromLocation: (instance: EditUserAccessComponent, locationId: string, locationUserId: string): void => {
          this.accountApiService.removeFromLocation(locationId, locationUserId)
            .pipe(
              take(1),
              tap(() => {
                this.alertsService.addSuccess('user removed from location', 'Success: ');
              }),
              catchError(() => {
                this.alertsService.addSuccess('an error occurred during removing user');

                return of(null);
              }),
            )
            .subscribe(() => {
              this.updateInstance(instance, user.id);
            });
        },
        onNavigationToLocationPage: (locationId: string): void => {
          this.locationSwitcherService.navigateToLocationPage(locationId, '/settings/location/users');
        },
      })
        .pipe(take(1))
        .subscribe(() => {
          subject.next(undefined);
          subject.complete();
        });
    });

    return subject.asObservable();
  }

  getLocations(callback: (info: [UserWithAccount[], Location[]]) => void): void {
    this.usersListService.load()
      .pipe(
        concatMap(() => this.usersListService.getUsersAndLocations()),
        take(1),
      )
      .subscribe((info) => {
        callback(info);
      });
  }

  private updateInstance(instance: EditUserAccessComponent, userId: string): void {
    this.getLocations(([updUsers, updLocations]) => {
      instance.user = updUsers.find(item => item.id === userId);
      instance.locations = updLocations.map(loc => ({
        id: loc._id,
        locationName: loc.name,
        enabled: loc.enabled,
      }));
      instance.refreshData();
    });
  }
}
